<template>
    <form @submit.prevent="submitForm">
        <div v-if="error" class="error-message-gcn">{{ error }}</div>

        <!-- Portfolio Name -->
        <div class="form-group-gcn">
            <label for="portfolio-name" class="text-primary">Nombre de Cartera <span class="required">*</span></label>
            <input 
                type="text" 
                id="portfolio-name" 
                class="form-control-gcn" 
                :class="{ 'error': shouldShowError('name') }"
                v-model="formData.name" 
                @input="validateNameRealTime"
                @blur="markFieldTouched('name'); validateName()"
                @focus="markFieldTouched('name')"
                required 
                maxlength="100"
                placeholder="Introduce el nombre de la cartera">
            <div class="input-feedback">
                <small v-if="shouldShowError('name')" class="error-message-gcn">{{ nameError }}</small>
                <small v-else-if="formData.name" class="character-count text-secondary">
                    {{ formData.name.length }}/100 caracteres
                </small>
            </div>
        </div>

        <!-- Initial Capital -->
        <div class="form-group-gcn" v-if="mode !== 'update'">
            <label for="portfolio-capital" class="text-primary">
                Capital Inicial <span class="required">*</span>
            </label>
            <input 
                type="text" 
                id="portfolio-capital" 
                class="form-control-gcn" 
                :class="{ 'error': shouldShowError('capital') }"
                :value="formatDisplayValue(formData.capital)"
                @input="event => handleNumericInput(event, 'capital')"
                @blur="event => { markFieldTouched('capital'); formatNumericOnBlur(event, 'capital'); }"
                @focus="event => { markFieldTouched('capital'); handleFocus(event, 'capital'); }"
                required 
                placeholder="0.00">
            <small class="text-secondary">{{ getCapitalHelperText() }}</small>
            <small v-if="shouldShowError('capital')" class="error-message-gcn">{{ capitalError }}</small>
        </div>

        <!-- Currency -->
        <div class="form-group-gcn">
            <label for="portfolio-currency" class="text-primary">Moneda <span class="required">*</span></label>
            <select 
                id="portfolio-currency" 
                class="form-control-gcn" 
                :class="{ 'error': shouldShowError('currency') }"
                v-model="formData.currency" 
                @change="markFieldTouched('currency'); validateCurrency()"
                @focus="markFieldTouched('currency')"
                required>
                <option value="">Selecciona una moneda</option>
                <option value="EUR">EUR - Euro</option>
                <option value="USD">USD - Dólar estadounidense</option>
                <option value="GBP">GBP - Libra esterlina</option>
                <option value="JPY">JPY - Yen japonés</option>
                <option value="CHF">CHF - Franco suizo</option>
                <option value="CAD">CAD - Dólar canadiense</option>
                <option value="AUD">AUD - Dólar australiano</option>
            </select>
            <small v-if="shouldShowError('currency')" class="error-message-gcn">{{ currencyError }}</small>
        </div>

        <!-- Creation Date -->
        <div class="form-group-gcn" v-if="mode !== 'update'">
            <label for="portfolio-date" class="text-primary">
                Fecha de Creación <span class="required">*</span>
            </label>
            <input 
                type="text" 
                id="portfolio-date" 
                class="form-control-gcn" 
                :class="{ 'error': shouldShowError('date') }"
                v-model="formData.date" 
                @input="formatDateInput" 
                @blur="markFieldTouched('date'); validateDate()"
                @focus="markFieldTouched('date')"
                required 
                placeholder="dd-mm-yyyy" 
                maxlength="10">
            <small class="text-secondary">Formato: día-mes-año (dd-mm-yyyy)</small>
            <small v-if="shouldShowError('date')" class="error-message-gcn">{{ dateError }}</small>
        </div>

        <!-- Risk Percentage -->
        <div class="form-group-gcn">
            <label for="portfolio-risk" class="text-primary">
                Porcentaje de Riesgo (%) <span class="required">*</span>
            </label>
            <input 
                type="text" 
                id="portfolio-risk" 
                class="form-control-gcn" 
                :class="{ 'error': shouldShowError('risk') }"
                :value="formatDisplayValue(formData.risk_percentage)"
                @input="event => handleNumericInput(event, 'risk_percentage')"
                @blur="event => { markFieldTouched('risk'); formatNumericOnBlur(event, 'risk_percentage'); }"
                @focus="event => { markFieldTouched('risk'); handleFocus(event, 'risk_percentage'); }"
                required 
                placeholder="2.00"
                min="0"
                max="100">
            <small class="text-secondary">Porcentaje del capital que estás dispuesto a arriesgar por operación (recomendado: 1-5%)</small>
            <small v-if="shouldShowError('risk')" class="error-message-gcn">{{ riskError }}</small>
        </div>

        <!-- Form Status Indicator -->
        <div v-if="isFormValid && submitAttempted" class="form-status success">
            <span class="status-icon">✓</span> Formulario completado correctamente
        </div>
        <div v-else-if="hasErrors && submitAttempted" class="form-status error">
            <span class="status-icon">⚠</span> Por favor, corrija los errores antes de continuar
        </div>

        <!-- Hidden submit button -->
        <button type="submit" style="display: none;"></button>
    </form>
</template>

<script>
import { formatDateDMY, parseDate } from './apiService';

export default {
    name: 'PortfolioForm',
    props: {
        mode: { type: String, required: true }, // 'create', 'update'
        initialData: { type: Object, default: () => ({}) },
        error: { type: String, default: null },
        isSubmitting: { type: Boolean, default: false }
    },
    data() {
        return {
            formData: {
                name: '',
                capital: null,
                currency: '',
                date: formatDateDMY(new Date()),
                risk_percentage: 2
            },
            // Validation errors
            nameError: null,
            capitalError: null,
            currencyError: null,
            dateError: null,
            riskError: null,
            // Validation states
            nameValid: false,
            capitalValid: false,
            currencyValid: false,
            dateValid: false,
            riskValid: false,
            // Touch states
            touchedFields: {
                name: false,
                capital: false,
                currency: false,
                date: false,
                risk: false
            },
            submitAttempted: false
        };
    },
    computed: {
        isFormValid() {
            let requiredFieldsValid = this.nameValid && this.currencyValid && this.riskValid;
            
            if (this.mode !== 'update') {
                requiredFieldsValid = requiredFieldsValid && this.capitalValid;
            }
            
            if (this.mode !== 'update') {
                requiredFieldsValid = requiredFieldsValid && this.dateValid;
            }
            
            return requiredFieldsValid && !this.hasErrors;
        },
        hasErrors() {
            return !!(this.nameError || this.capitalError || this.currencyError || this.dateError || this.riskError);
        }
    },
    created() {
        this.initializeFormData();
        this.validateInitialValues();
    },
    methods: {
        markFieldTouched(fieldName) {
            this.touchedFields[fieldName] = true;
        },

        shouldShowError(fieldName) {
            return (this.touchedFields[fieldName] || this.submitAttempted) && this[fieldName + 'Error'];
        },

        initializeFormData() {
            if (this.initialData) {
                this.formData = {
                    name: this.initialData.name || '',
                    capital: this.initialData.capital || null,
                    currency: this.initialData.currency || '',
                    date: this.initialData.date || formatDateDMY(new Date()),
                    risk_percentage: this.initialData.risk_percentage || 2
                };
            }
        },

        validateInitialValues() {
            if (this.formData.name) this.validateNameRealTime();
            if (this.formData.capital !== null) this.validateCapitalRealTime();
            if (this.formData.currency) this.validateCurrency();
            if (this.formData.date) this.validateDateRealTime();
            if (this.formData.risk_percentage !== null) this.validateRiskRealTime();
        },

        getCapitalHelperText() {
            switch (this.mode) {
                case 'create':
                    return 'Capital con el que empiezas la cartera.';
                case 'update':
                    return 'Capital actual de la cartera.';
                default:
                    return '';
            }
        },

        validateName() {
            this.validateNameRealTime();
        },

        validateNameRealTime() {
            const trimmedName = this.formData.name?.trim();
            if (!trimmedName) {
                this.nameError = 'El nombre de la cartera es requerido.';
                this.nameValid = false;
            } else if (trimmedName.length < 3) {
                this.nameError = 'El nombre debe tener al menos 3 caracteres.';
                this.nameValid = false;
            } else if (trimmedName.length > 100) {
                this.nameError = 'El nombre no puede exceder 100 caracteres.';
                this.nameValid = false;
            } else {
                this.nameError = null;
                this.nameValid = true;
                // Update the form data with trimmed value
                this.formData.name = trimmedName;
            }
        },

        validateCapital() {
            this.validateCapitalRealTime();
        },

        validateCapitalRealTime() {
            const capital = parseFloat(this.formData.capital);
            
            if (this.formData.capital === null || this.formData.capital === undefined || 
                this.formData.capital === '' || isNaN(capital)) {
                this.capitalError = 'El capital es requerido y debe ser un número válido.';
                this.capitalValid = false;
            } else if (capital < 0) {
                this.capitalError = 'El capital no puede ser negativo.';
                this.capitalValid = false;
            } else if (capital <= 0) {
                this.capitalError = 'El capital debe ser mayor que 0.';
                this.capitalValid = false;
            } else if (capital > 1000000000) {
                this.capitalError = 'El capital no puede exceder 1.000.000.000.';
                this.capitalValid = false;
            } else {
                this.capitalError = null;
                this.capitalValid = true;
            }
        },

        validateCurrency() {
            if (!this.formData.currency) {
                this.currencyError = 'Debe seleccionar una moneda.';
                this.currencyValid = false;
            } else {
                this.currencyError = null;
                this.currencyValid = true;
            }
        },

        validateDate() {
            this.validateDateRealTime();
        },

        validateDateRealTime() {
            if (this.mode === 'update') {
                this.dateValid = true;
                this.dateError = null;
                return;
            }

            if (!this.formData.date) {
                this.dateError = 'La fecha es requerida.';
                this.dateValid = false;
                return;
            }

            // Normalize date (add leading zeros)
            const parts = this.formData.date.split('-');
            if (parts.length === 3) {
                parts[0] = parts[0].padStart(2, '0');
                parts[1] = parts[1].padStart(2, '0');
                const normalizedDate = parts.join('-');
                if (normalizedDate !== this.formData.date) {
                    this.formData.date = normalizedDate;
                }
            }

            const dateRegex = /^\d{2}-\d{2}-\d{4}$/;
            if (!dateRegex.test(this.formData.date)) {
                this.dateError = 'La fecha debe estar en formato dd-mm-yyyy.';
                this.dateValid = false;
                return;
            }

            const parsedDate = parseDate(this.formData.date);
            if (!parsedDate) {
                this.dateError = 'La fecha ingresada no es válida.';
                this.dateValid = false;
                return;
            }

            // Check if date is not in the future
            const today = new Date();
            today.setHours(23, 59, 59, 999);
            if (parsedDate > today) {
                this.dateError = 'La fecha no puede ser futura.';
                this.dateValid = false;
                return;
            }

            this.dateError = null;
            this.dateValid = true;
        },

        validateRisk() {
            this.validateRiskRealTime();
        },

        validateRiskRealTime() {
            const risk = parseFloat(this.formData.risk_percentage);
            if (this.formData.risk_percentage === null || this.formData.risk_percentage === undefined || 
                this.formData.risk_percentage === '' || isNaN(risk)) {
                this.riskError = 'El porcentaje de riesgo es requerido y debe ser un número válido.';
                this.riskValid = false;
            } else if (risk < 0) {
                this.riskError = 'El porcentaje de riesgo no puede ser negativo.';
                this.riskValid = false;
            } else if (risk > 100) {
                this.riskError = 'El porcentaje de riesgo no puede ser mayor que 100%.';
                this.riskValid = false;
            } else {
                this.riskError = null;
                this.riskValid = true;
            }
        },

        formatDateInput(event) {
            let input = event.target;
            let cursorPos = input.selectionStart;
            let oldValue = this.formData.date || '';
            let digits = input.value.replace(/\D/g, '');

            let newValue = '';
            if (digits.length > 4) {
                newValue = digits.slice(0, 2) + '-' + digits.slice(2, 4) + '-' + digits.slice(4, 8);
            } else if (digits.length > 2) {
                newValue = digits.slice(0, 2) + '-' + digits.slice(2, 4);
            } else {
                newValue = digits;
            }

            this.formData.date = newValue;
            this.validateDateRealTime();

            this.$nextTick(() => {
                let originalDigitCount = 0;
                for(let i = 0; i < cursorPos; i++) {
                    if (/\d/.test(oldValue[i])) {
                        originalDigitCount++;
                    }
                }

                let finalPos = 0;
                let currentDigitCount = 0;
                for(let i = 0; i < newValue.length; i++) {
                    finalPos = i + 1;
                    if (/\d/.test(newValue[i])) {
                        currentDigitCount++;
                    }
                    if (currentDigitCount >= originalDigitCount) {
                        if (cursorPos > 0 && oldValue.length > newValue.length && oldValue[cursorPos - 1] === '-' && !/\d/.test(newValue[finalPos-1])) {
                           finalPos--;
                        }
                        break; 
                    }
                }
                if (originalDigitCount === 0) finalPos = 0;
                finalPos = Math.max(0, Math.min(finalPos, newValue.length));

                input.selectionStart = finalPos;
                input.selectionEnd = finalPos;
            });
        },

        formatDisplayValue(value) {
            if (value === null || value === undefined) return '';
            if (typeof value === 'number') {
                return value.toFixed(2);
            }
            return value;
        },

        handleFocus(event, modelKey) {
            if (this.formData[modelKey] !== null && this.formData[modelKey] !== undefined) {
                // Display raw value with dots for editing
                event.target.value = this.formData[modelKey].toString();
            }
        },

        handleNumericInput(event, modelKey) {
            let value = event.target.value;
            // Only allow digits, dots, and commas (no dashes/minus signs)
            let sanitizedValue = value.replace(/[^\d.,]/g, '');
            
            // Handle decimal separators: find the last comma or dot as decimal separator
            const lastCommaIndex = sanitizedValue.lastIndexOf(',');
            const lastDotIndex = sanitizedValue.lastIndexOf('.');
            const lastDecimalIndex = Math.max(lastCommaIndex, lastDotIndex);
            
            if (lastDecimalIndex !== -1) {
                // Split into integer and decimal parts
                const integerPart = sanitizedValue.substring(0, lastDecimalIndex).replace(/[.,]/g, ''); // Remove all dots/commas from integer part
                const decimalPart = sanitizedValue.substring(lastDecimalIndex + 1);
                
                // Limit decimal part to 2 digits
                const limitedDecimalPart = decimalPart.substring(0, 2);
                
                // Reconstruct with dot as decimal separator
                // Always include the dot if there was a decimal separator, even if decimal part is empty
                sanitizedValue = integerPart + '.' + limitedDecimalPart;
            }
            
            // Prevent starting with decimal point - require leading zero
            if (sanitizedValue.startsWith('.')) {
                sanitizedValue = '0' + sanitizedValue;
            }

            event.target.value = sanitizedValue;

            if (sanitizedValue === '') {
                this.formData[modelKey] = null;
            } else if (!isNaN(parseFloat(sanitizedValue)) && isFinite(sanitizedValue)) {
                this.formData[modelKey] = sanitizedValue;
            } else if (sanitizedValue.endsWith('.')) {
                this.formData[modelKey] = sanitizedValue;
            } else {
                this.formData[modelKey] = null;
            }

            // Real-time validation
            if (modelKey === 'capital') {
                this.validateCapitalRealTime();
            } else if (modelKey === 'risk_percentage') {
                this.validateRiskRealTime();
            }
        },

        formatNumericOnBlur(event, modelKey) {
            let value = this.formData[modelKey];
            
            if (value === null || String(value).trim() === '') {
                this.formData[modelKey] = null;
                return;
            }
            
            if(typeof value === 'string' && value.endsWith('.')) {
                value = value.slice(0, -1);
            }

            const numericValue = parseFloat(value);
            if (!isNaN(numericValue) && isFinite(numericValue)) {
                // Round to 2 decimal places
                const roundedValue = Math.round(numericValue * 100) / 100;
                this.formData[modelKey] = roundedValue;
            } else {
                this.formData[modelKey] = null;
            }

            // Validate after formatting
            if (modelKey === 'capital') {
                this.validateCapitalRealTime();
            } else if (modelKey === 'risk_percentage') {
                this.validateRiskRealTime();
            }
        },

        submitForm() {
            this.submitAttempted = true;
            
            // Mark all relevant fields as touched
            this.markFieldTouched('name');
            this.markFieldTouched('currency');
            this.markFieldTouched('risk');
            
            if (this.mode !== 'update') {
                this.markFieldTouched('capital');
            }
            
            if (this.mode !== 'update') {
                this.markFieldTouched('date');
            }

            // Validate all fields
            this.validateName();
            this.validateCurrency();
            this.validateRisk();
            
            if (this.mode !== 'update') {
                this.validateCapital();
            }
            
            if (this.mode !== 'update') {
                this.validateDate();
            }

            if (!this.isFormValid || this.hasErrors) {
                this.$emit('error', 'Por favor, corrija los errores en el formulario antes de continuar.');
                return;
            }

            // Prepare data based on mode
            let dataToSend = {
                name: this.formData.name.trim(),
                currency: this.formData.currency,
                risk: parseFloat(this.formData.risk_percentage)
            };

            if (this.mode !== 'update') {
                dataToSend.initial_capital = parseFloat(this.formData.capital);
            }

            if (this.mode !== 'update') {
                dataToSend.created_date = this.formData.date;
            }

            this.$emit('submit', dataToSend);
        }
    },
    watch: {
        initialData: {
            handler() {
                this.initializeFormData();
                this.validateInitialValues();
            },
            deep: true
        },
        mode() {
            this.validateInitialValues();
        }
    }
}
</script>

<style scoped>
/* Enhanced validation styles */
.required {
    color: var(--color-error);
    font-weight: bold;
}

.error-message-gcn {
    color: var(--color-error);
    font-size: 0.8em;
    display: block;
    margin-top: 4px;
}

.text-primary {
    color: var(--color-text-primary);
}

.text-secondary {
    color: var(--color-text-secondary);
}

.form-control-gcn.error {
    border-color: var(--color-error);
    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
}

.input-feedback {
    margin-top: 4px;
}

.character-count {
    float: right;
    font-size: 0.75em;
}

.form-status {
    padding: 10px;
    border-radius: 4px;
    margin-top: 15px;
    font-size: 0.9em;
    display: flex;
    align-items: center;
    gap: 8px;
}

.form-status.success {
    background-color: #d4edda;
    border: 1px solid #c3e6cb;
    color: #155724;
}

.form-status.error {
    background-color: #f8d7da;
    border: 1px solid #f5c6cb;
    color: #721c24;
}

.status-icon {
    font-weight: bold;
    font-size: 1.1em;
}
</style>